import { DATADOG_RUM_NAME, useConsent } from '@lichtblick/consent';
import { logger } from '@lichtblick/logger';
import { useEffect } from 'react';
import { useLoggedIn } from '@/tracking/use-logged-in';
import { sanitizeStateForMonitoring } from '../monitoring/sanitizeStateForMonitoring';

type DataDogProps = {
  loggedIn?: boolean;
  state: Record<string, unknown>;
  viewName: string;
};

type DataDogEnergyCheckoutProps = DataDogProps & {
  checkout: 'Energy';
  energyCheckoutType: 'Bundle' | 'Electricity' | 'Gas';
};

type DataDogOtherCheckoutProps = DataDogProps & {
  checkout: 'Heating' | 'Dynamic' | 'Vario' | 'EaaS' | 'Energy Solutions';
};

export const useDataDog = ({
  checkout,
  state,
  viewName,
  ...props
}: DataDogEnergyCheckoutProps | DataDogOtherCheckoutProps) => {
  const datadogRumState = useConsent(DATADOG_RUM_NAME);

  const loggedIn = useLoggedIn();
  const currentCheckout = 'energyCheckoutType' in props ? props.energyCheckoutType : checkout;

  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO
  useEffect(() => {
    if (!datadogRumState) {
      return;
    }

    import('@datadog/browser-rum').then(({ datadogRum }) => {
      datadogRum.onReady(() => {
        datadogRum.startView({
          name: `${currentCheckout}${loggedIn ? ' (Existing)' : ''}: ${viewName}`,
          service: process.env.NEXT_PUBLIC_DATADOG_SERVICE_NAME,
          version: process.env.NEXT_PUBLIC_VERSION,
        });

        datadogRum.setGlobalContextProperty(`${currentCheckout.toLowerCase()}State`, sanitizeStateForMonitoring(state));

        datadogRum.startSessionReplayRecording();
      });
    });
  }, [datadogRumState, viewName, currentCheckout]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO
  useEffect(() => {
    logger.info(`[${checkout}${loggedIn ? ' (Existing)' : ''}] Viewed ${viewName}`, {
      state: sanitizeStateForMonitoring(state),
      viewName,
      checkout,
      loggedIn,
    });
  }, [checkout, viewName]);
};
